import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { ArrowConfigDef, ArrowIdDef } from '@alchemiesolns/arrow-def';
import { buildIndexMaps } from './atomContainer';

/**
 * Resolve an opaque ID to its type (atom or bond) and current index.
 * Index is relative to Map iteration order — not canonical.
 */
function resolveId(
  atomIdToIndex: Map<string, number>,
  bondIdToIndex: Map<string, number>,
  id: string,
): { type: 'atom' | 'bond'; index: number } {
  const atomIndex = atomIdToIndex.get(id);
  if (atomIndex !== undefined) {
    return { type: 'atom', index: atomIndex };
  }

  const bondIndex = bondIdToIndex.get(id);
  if (bondIndex !== undefined) {
    return { type: 'bond', index: bondIndex };
  }

  throw new Error(`ID not found in atoms or bonds: ${id}`);
}

/**
 * Convert a single ID-based arrow to index-based for serialization or rendering.
 * Indices are relative to the workspace's current Map iteration order.
 */
export function resolveArrowIds(
  workspace: MolecularWorkspace,
  arrow: ArrowIdDef,
): ArrowConfigDef {
  const { atomIdToIndex, bondIdToIndex } = buildIndexMaps(workspace);

  const src = resolveId(atomIdToIndex, bondIdToIndex, arrow.srcId);
  const dst = resolveId(atomIdToIndex, bondIdToIndex, arrow.dstId);

  const result: ArrowConfigDef = {
    type: arrow.type,
    src: src.index,
    dst: dst.index,
  };

  if (arrow.anchorId) {
    const anchor = resolveId(atomIdToIndex, bondIdToIndex, arrow.anchorId);
    result.p = anchor.index;
  }

  return result;
}

/**
 * Convert all workspace arrows from ID-based to index-based.
 * Arrows referencing stale IDs are silently skipped.
 */
export function resolveAllArrowIds(
  workspace: MolecularWorkspace,
): ArrowConfigDef[] {
  const { atomIdToIndex, bondIdToIndex } = buildIndexMaps(workspace);
  const resolved: ArrowConfigDef[] = [];

  for (const arrow of workspace.getArrows()) {
    try {
      const src = resolveId(atomIdToIndex, bondIdToIndex, arrow.srcId);
      const dst = resolveId(atomIdToIndex, bondIdToIndex, arrow.dstId);

      const result: ArrowConfigDef = {
        type: arrow.type,
        src: src.index,
        dst: dst.index,
      };

      if (arrow.anchorId) {
        const anchor = resolveId(atomIdToIndex, bondIdToIndex, arrow.anchorId);
        result.p = anchor.index;
      }

      resolved.push(result);
    } catch {
      // Arrow references a stale atom/bond ID — skip it
    }
  }

  return resolved;
}
